if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        /*
         * Optional Fall Sale upgrade
         * --------------------------
         * The primary product is still submitted using its original FormData.
         * This intentionally preserves Infinite Options and any other line-item
         * properties already attached to the product form.
         *
         * If the shopper selected a Fall Sale upgrade, the promotional variant
         * is added immediately afterward with the same quantity as the primary
         * product. If no upgrade is selected, the original product-form behavior
         * remains unchanged.
         */
        const productInfo = this.closest('product-info');
        const fallSaleBlock = productInfo?.querySelector('[data-fall-sale-upgrade]');
        const selectedFallSaleVariant = fallSaleBlock?.querySelector('[data-fall-sale-variant]:checked');
        const fallSaleVariantId = selectedFallSaleVariant?.value || null;

        const submittedQuantity = parseInt(formData.get('quantity'), 10);
        const fallSaleQuantity = Number.isFinite(submittedQuantity) && submittedQuantity > 0 ? submittedQuantity : 1;

        /*
         * When an optional promotional upgrade is selected, give the qualifying
         * product and its upgrade a shared private group ID. Shopify private
         * line-item properties (leading underscore) remain available to theme
         * logic without being presented as normal shopper-facing options.
         *
         * A unique ID also prevents separate qualifying purchases from being
         * merged together when they need independent upgrade relationships.
         */
        let inlinePromotionGroupId = null;

        if (fallSaleVariantId) {
          inlinePromotionGroupId =
            window.crypto && typeof window.crypto.randomUUID === 'function'
              ? window.crypto.randomUUID()
              : `promo-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

          formData.append('properties[_inline_promo_group_id]', inlinePromotionGroupId);
          formData.append('properties[_inline_promo_role]', 'qualifying-product');
        }

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        let response;

        try {
          const mainProductRequest = await fetch(`${routes.cart_add_url}`, config);
          response = await mainProductRequest.json();

          if (response.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: response.errors || response.description,
              message: response.message,
            });
            this.handleErrorMessage(response.description);

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButtonText.classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return;
          }

          /*
           * Only add the Fall Sale promotional variant when the shopper
           * explicitly selected one. Nothing is added silently.
           *
           * This is intentionally a second Ajax request rather than rebuilding
           * the primary product into an items[] JSON payload. Keeping the
           * primary request untouched protects Infinite Options fields and other
           * existing product-form integrations.
           */
          if (fallSaleVariantId) {
            const promotionPayload = {
              items: [
                {
                  id: Number(fallSaleVariantId),
                  quantity: fallSaleQuantity,
                  properties: {
                    _inline_promo_group_id: inlinePromotionGroupId,
                    _inline_promo_role: 'upgrade',
                  },
                },
              ],
            };

            if (this.cart) {
              promotionPayload.sections = this.cart.getSectionsToRender().map((section) => section.id);
              promotionPayload.sections_url = window.location.pathname;
            }

            const promotionRequest = await fetch(`${routes.cart_add_url}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
              },
              body: JSON.stringify(promotionPayload),
            });

            const promotionResponse = await promotionRequest.json();

            if (promotionResponse.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: fallSaleVariantId,
                errors: promotionResponse.errors || promotionResponse.description,
                message: promotionResponse.message,
              });

              this.handleErrorMessage(
                'Your product was added to the cart, but the free promotional upgrade could not be added.'
              );
            } else {
              /*
               * Preserve the primary product's cart-notification-product section
               * because CartNotification.renderContents() uses response.key to
               * locate that exact line item inside the returned section HTML.
               *
               * The second promotional request renders that section for the
               * promotional item instead. Replacing the whole sections object
               * would leave the primary product key paired with promotional
               * markup, causing CartNotification to fail before it reaches the
               * cart-icon-bubble update.
               *
               * Therefore:
               * - keep the primary product notification section;
               * - use the promotional request's FINAL cart-state HTML for every
               *   other returned section (cart icon, cart button, cart drawer,
               *   etc.).
               */
              const finalSections = { ...(response.sections || {}) };

              Object.entries(promotionResponse.sections || {}).forEach(([sectionId, html]) => {
                if (sectionId !== 'cart-notification-product') {
                  finalSections[sectionId] = html;
                }
              });

              response = {
                ...response,
                sections: finalSections,
                items: promotionResponse.items || response.items,
              };
            }
          }

          if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
          if (!this.error)
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: response,
            }).then(() => {
              CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
            });

          this.error = false;
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener(
              'modalClosed',
              () => {
                setTimeout(() => {
                  CartPerformance.measure('add:paint-updated-sections', () => {
                    this.cart.renderContents(response);
                  });
                });
              },
              { once: true }
            );
            quickAddModal.hide(true);
          } else {
            CartPerformance.measure('add:paint-updated-sections', () => {
              this.cart.renderContents(response);
            });
          }
        } catch (e) {
          console.error(e);
        } finally {
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner').classList.add('hidden');

          CartPerformance.measureFromEvent('add:user-action', evt);
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}